import React, { useState } from 'react'
import { Calendar, Filter, X, ChevronDown, Plus } from 'lucide-react'

export interface DateRangeOption {
  label: string
  value: string
  days: number
}

export interface FilterOption {
  label: string
  value: string
}

export interface FilterDimension {
  key: string
  label: string
  options: FilterOption[]
}

interface AnalyticsFiltersProps {
  // Date range
  selectedDateRange: string
  onDateRangeChange: (range: string) => void
  dateRangeOptions: DateRangeOption[]

  // Comparison
  comparisonEnabled: boolean
  onComparisonToggle: (enabled: boolean) => void
  comparisonType: 'previous-period' | 'previous-year' | 'custom'
  onComparisonTypeChange: (type: 'previous-period' | 'previous-year' | 'custom') => void

  // Filters
  availableFilters: FilterDimension[]
  activeFilters: Record<string, string[]>
  onFilterChange: (filterKey: string, values: string[]) => void
  onClearFilters: () => void
}

export function AnalyticsFilters({
  selectedDateRange,
  onDateRangeChange,
  dateRangeOptions,
  comparisonEnabled,
  onComparisonToggle,
  comparisonType,
  onComparisonTypeChange,
  availableFilters,
  activeFilters,
  onFilterChange,
  onClearFilters
}: AnalyticsFiltersProps) {
  const [showDateMenu, setShowDateMenu] = useState(false)
  const [showCompareMenu, setShowCompareMenu] = useState(false)
  const [showFilterMenus, setShowFilterMenus] = useState<Record<string, boolean>>({})

  const hasActiveFilters = Object.values(activeFilters).some(values => values.length > 0)
  const selectedDateOption = dateRangeOptions.find(o => o.value === selectedDateRange)

  const toggleFilterValue = (filterKey: string, value: string) => {
    const current = activeFilters[filterKey] || []
    const newValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]
    onFilterChange(filterKey, newValues)
  }

  return (
    <div className="space-y-4 mb-6">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Date Range Button */}
        <div className="relative">
          <button
            onClick={() => setShowDateMenu(!showDateMenu)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-dark-light border border-brand-gray/20 rounded-monumenta text-white text-sm hover:border-brand-primary/50 transition-colors"
          >
            <Calendar size={16} />
            <span>{selectedDateOption?.label || 'Selecionar período'}</span>
            <ChevronDown size={14} />
          </button>

          {showDateMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowDateMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-64 bg-brand-dark-light border border-brand-gray/20 rounded-monumenta shadow-lg z-50 overflow-hidden">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onDateRangeChange(option.value)
                      setShowDateMenu(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      selectedDateRange === option.value
                        ? 'bg-brand-primary/20 text-brand-primary'
                        : 'text-white hover:bg-brand-dark'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Compare Button */}
        <div className="relative">
          <button
            onClick={() => setShowCompareMenu(!showCompareMenu)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-monumenta text-sm transition-colors ${
              comparisonEnabled
                ? 'bg-brand-primary/10 border-brand-primary/50 text-brand-primary'
                : 'bg-brand-dark-light border-brand-gray/20 text-white hover:border-brand-primary/50'
            }`}
          >
            <span>Comparar</span>
            <ChevronDown size={14} />
          </button>

          {showCompareMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowCompareMenu(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-72 bg-brand-dark-light border border-brand-gray/20 rounded-monumenta shadow-lg z-50 p-4">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={comparisonEnabled}
                      onChange={(e) => onComparisonToggle(e.target.checked)}
                      className="w-4 h-4 rounded border-brand-gray/20 bg-brand-dark text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                    />
                    <span className="text-sm text-white">Ativar comparação</span>
                  </label>

                  {comparisonEnabled && (
                    <div className="space-y-2 pl-6 border-l-2 border-brand-primary/30">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="comparison-type"
                          value="previous-period"
                          checked={comparisonType === 'previous-period'}
                          onChange={(e) => onComparisonTypeChange('previous-period')}
                          className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                        />
                        <span className="text-sm text-brand-gray">Período anterior</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="comparison-type"
                          value="previous-year"
                          checked={comparisonType === 'previous-year'}
                          onChange={(e) => onComparisonTypeChange('previous-year')}
                          className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                        />
                        <span className="text-sm text-brand-gray">Ano anterior</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer opacity-50">
                        <input
                          type="radio"
                          name="comparison-type"
                          value="custom"
                          disabled
                          className="w-4 h-4 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                        />
                        <span className="text-sm text-brand-gray">Personalizado (em breve)</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Add Filter Button - Opens dimension selector */}
        {availableFilters.length > 0 && (
          <AddFilterButton
            availableFilters={availableFilters}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            showFilterMenus={showFilterMenus}
            setShowFilterMenus={setShowFilterMenus}
            toggleFilterValue={toggleFilterValue}
          />
        )}

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-brand-gray hover:text-white transition-colors"
          >
            <X size={14} />
            Limpar tudo
          </button>
        )}
      </div>

      {/* Active filters chips */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-brand-gray">Filtros ativos:</span>
          {Object.entries(activeFilters).map(([filterKey, values]) => {
            if (values.length === 0) return null
            const filter = availableFilters.find(f => f.key === filterKey)
            if (!filter) return null

            return values.map(value => {
              const option = filter.options.find(o => o.value === value)
              return (
                <div
                  key={`${filterKey}-${value}`}
                  className="flex items-center gap-2 px-3 py-1 bg-brand-primary/10 border border-brand-primary/30 rounded-full text-xs text-brand-primary"
                >
                  <span>{filter.label}: {option?.label || value}</span>
                  <button
                    onClick={() => {
                      const newValues = values.filter(v => v !== value)
                      onFilterChange(filterKey, newValues)
                    }}
                    className="hover:text-white transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              )
            })
          })}
        </div>
      )}
    </div>
  )
}

// Add Filter Button Component - Google Search Console style
interface AddFilterButtonProps {
  availableFilters: FilterDimension[]
  activeFilters: Record<string, string[]>
  onFilterChange: (filterKey: string, values: string[]) => void
  showFilterMenus: Record<string, boolean>
  setShowFilterMenus: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  toggleFilterValue: (filterKey: string, value: string) => void
}

function AddFilterButton({
  availableFilters,
  activeFilters,
  onFilterChange,
  showFilterMenus,
  setShowFilterMenus,
  toggleFilterValue
}: AddFilterButtonProps) {
  const [showDimensionSelector, setShowDimensionSelector] = useState(false)

  // Track which dimensions have filters active OR are currently being edited
  const activeDimensions = Object.keys(activeFilters).filter(key => activeFilters[key].length > 0)
  const visibleDimensions = Object.keys(showFilterMenus).filter(key => showFilterMenus[key])

  return (
    <>
      {/* Dimension filter pills - show for active filters OR currently open menus */}
      {[...new Set([...activeDimensions, ...visibleDimensions])].map(dimensionKey => {
        const dimension = availableFilters.find(f => f.key === dimensionKey)
        if (!dimension) return null

        const activeCount = activeFilters[dimensionKey]?.length || 0
        const isOpen = showFilterMenus[dimensionKey]
        const hasActiveFilters = activeCount > 0

        return (
          <div key={dimensionKey} className="relative">
            <button
              onClick={() => setShowFilterMenus(prev => ({
                ...prev,
                [dimensionKey]: !prev[dimensionKey]
              }))}
              className={`flex items-center gap-2 px-4 py-2 border rounded-monumenta text-sm transition-colors ${
                hasActiveFilters
                  ? 'bg-brand-primary/10 border-brand-primary/50 text-brand-primary hover:bg-brand-primary/20'
                  : 'bg-brand-dark-light border-brand-gray/20 text-white hover:border-brand-primary/50'
              }`}
            >
              <Filter size={14} />
              <span>{dimension.label}</span>
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 bg-brand-primary/20 rounded-full text-xs">
                  {activeCount}
                </span>
              )}
              <ChevronDown size={14} />
            </button>

            {isOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowFilterMenus(prev => ({
                    ...prev,
                    [dimensionKey]: false
                  }))}
                />
                <div className="absolute top-full left-0 mt-2 w-80 bg-brand-dark-light border border-brand-gray/20 rounded-monumenta shadow-lg z-50">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-brand-gray/20 flex items-center justify-between">
                    <span className="text-sm font-medium text-white">{dimension.label}</span>
                    <div className="flex items-center gap-3">
                      {hasActiveFilters && (
                        <button
                          onClick={() => {
                            onFilterChange(dimensionKey, [])
                          }}
                          className="text-xs text-brand-gray hover:text-white transition-colors"
                        >
                          Limpar
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowFilterMenus(prev => ({
                            ...prev,
                            [dimensionKey]: false
                          }))
                        }}
                        className="text-xs text-brand-gray hover:text-white transition-colors"
                      >
                        Fechar
                      </button>
                    </div>
                  </div>

                  {/* Options list */}
                  <div className="max-h-80 overflow-y-auto p-2">
                    {dimension.options.map(option => {
                      const isActive = activeFilters[dimensionKey]?.includes(option.value)
                      return (
                        <label
                          key={option.value}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-brand-dark rounded cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => toggleFilterValue(dimensionKey, option.value)}
                            className="w-4 h-4 rounded border-brand-gray/20 bg-brand-dark text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
                          />
                          <span className="text-sm text-white">{option.label}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )
      })}

      {/* Add Filter Button */}
      <div className="relative">
        <button
          onClick={() => setShowDimensionSelector(!showDimensionSelector)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-dark-light border border-brand-gray/20 text-white rounded-monumenta text-sm hover:border-brand-primary/50 transition-colors"
        >
          <Plus size={16} />
          <span>Adicionar filtro</span>
        </button>

        {showDimensionSelector && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowDimensionSelector(false)}
            />
            <div className="absolute top-full left-0 mt-2 w-64 bg-brand-dark-light border border-brand-gray/20 rounded-monumenta shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-brand-gray/20">
                <span className="text-sm font-medium text-white">Selecione uma dimensão</span>
              </div>
              <div className="p-2">
                {availableFilters.map(filter => {
                  const isActiveOrVisible = activeDimensions.includes(filter.key) || visibleDimensions.includes(filter.key)
                  return (
                    <button
                      key={filter.key}
                      onClick={() => {
                        if (!isActiveOrVisible) {
                          setShowDimensionSelector(false)
                          setShowFilterMenus(prev => ({
                            ...prev,
                            [filter.key]: true
                          }))
                        }
                      }}
                      disabled={isActiveOrVisible}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                        isActiveOrVisible
                          ? 'text-brand-gray/50 cursor-not-allowed'
                          : 'text-white hover:bg-brand-dark cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{filter.label}</span>
                        {isActiveOrVisible && (
                          <span className="text-xs text-brand-primary">Ativo</span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
